import axiosInstance from './axiosConfig';
import axios from 'axios';

// Create a separate axios instance for mTier server endpoints
const mTierAxios = axios.create({
  baseURL: process.env.REACT_APP_MULTI_LEVEL_BASE_URL || 'http://localhost:4000',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: false,
  timeout: 30000
});

// Add request interceptor for mTier axios
mTierAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('customer_auth_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Customer Authentication API
 */

// Send OTP to customer (email or phone)
export const sendCustomerOTP = async (merchantId, customerId, method = 'email', contact = null) => {
  try {
    const payload = {
      merchant_id: merchantId,
      customer_id: customerId,
      method: method, // 'email', 'phone', or 'whatsapp'
      email: method === 'email' ? contact : null,
      phone_number: (method === 'phone' || method === 'whatsapp') ? contact : null
    };

    const response = await mTierAxios.post('/auth/customer/request-otp', payload);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Verify OTP and get authentication token
export const verifyCustomerOTP = async (merchantId, otpId, otpCode, method, contact) => {
  try {
    const payload = {
      merchant_id: merchantId,
      otp_id: otpId,
      otp_code: otpCode,
      email: method === 'email' ? contact : null,
      phone_number: (method === 'phone' || method === 'whatsapp') ? contact : null
    };

    const response = await mTierAxios.post('/auth/customer/verify-otp', payload);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Create a new customer directly via Rails endpoint (post-OTP verification)
export const addUserDirectUrewards = async (merchantId, firstName, email, phoneNumber) => {
  try {
    const payload = {
      data: {
        phone_number: phoneNumber || "",
        email: email || "",
        first_name: firstName,
        sex: "-",
        source: "customer_portal"
      }
    };

    const response = await axiosInstance.post(`/api/v2/add_user_direct_urewards?merchant_id=${merchantId}`, payload);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Find customer by email or phone
export const findCustomerByContact = async (merchantId, contact) => {
  if (!contact) return null;
  try {
    const isEmail = String(contact).includes('@');
    const field = isEmail ? 'email' : 'phone';

    const response = await axiosInstance.get('/api/v2/get_urewards_customer', {
      params: { merchant_id: merchantId, [field]: contact }
    });
    return response.data;
  } catch (error) {
    console.log("Customer lookup:", error?.message || error);
    return null;
  }
};

/**
 * Provision a loyalty ID for a customer who doesn't have one yet.
 *
 * Calls POST /api/v2/provision_loyalty_id — a public endpoint that:
 *   1. Generates a fresh 13-digit loyalty code for the merchant
 *   2. Assigns it atomically to the customer
 *   3. Is idempotent: returns the existing code if one was already assigned
 *
 * @param {string|number} merchantId  - The merchant's ID
 * @param {string|number} customerId  - The customer's internal DB ID (not loyalty_id)
 * @returns {string|null}             - The provisioned loyalty_id code, or null on failure
 */
export const provisionLoyaltyId = async (merchantId, customerId) => {
  if (!merchantId || !customerId) return null;
  try {
    const response = await axiosInstance.post('/api/v2/provision_loyalty_id', {
      merchant_id: merchantId,
      customer_id: customerId
    });
    return response.data?.loyalty_id || null;
  } catch (error) {
    console.error('provisionLoyaltyId error:', error?.response?.data || error?.message || error);
    return null;
  }
};

// Get customer details and loyalty data
export const getCustomerDetails = async (customerId) => {
  const query = `
    query getCustomerPointDetails($customer_id: Int) {
      getCustomerPointDetails(customer_id: $customer_id) {
        simple_points {
          accumulated_points
          merchant_loyalty_program_id
          program_name
          threshold
          reward
          program_type
        }
        stamps_program {
          accumulated_stamps
          merchant_loyalty_program_id
          program_name
          threshold
          reward
          program_type
        }
      }
    }
  `;

  try {
    const response = await axiosInstance.post('/graphql', {
      query,
      variables: { customer_id: Number(customerId) }
    });

    const data = response.data?.data?.getCustomerPointDetails;
    return {
      points: data?.simple_points || [],
      tiers: data?.stamps_program || []
    };
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Utility helpers
export const maskEmail = (email) => {
  if (!email || !email.includes('@')) return email;
  const [username, domain] = email.split('@');
  const maskedUsername = username[0] + '***' + username[username.length - 1];
  return `${maskedUsername}@${domain}`;
};

export const maskPhoneNumber = (phone) => {
  if (!phone) return phone;
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.slice(0, 3) + '***' + cleaned.slice(-3);
};

export const getCustomerAuthToken = () => {
  try {
    return localStorage.getItem('customer_auth_token') || null;
  } catch (e) {
    return null;
  }
};

export const setCustomerAuthToken = (token, expiresIn = 3600) => {
  try {
    const expiresAt = new Date();
    expiresAt.setSeconds(expiresAt.getSeconds() + expiresIn);
    const data = { token, expiresAt: expiresAt.toISOString() };
    localStorage.setItem('customer_loyalty_auth', JSON.stringify(data));
    localStorage.setItem('customer_auth_token', token);
  } catch (e) {
    console.error('Error storing auth token:', e);
  }
};
